import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Download, ChevronDown, ChevronRight } from "lucide-react";
import ApiService from "../../services/api";

interface DishRecord {
  _id: string;
  dish_record: {
    available: boolean;
    cycle_type: string;
    dish_uuid: string;
    egg_age: number;
    identifier_1: string;
    identifier_2: string;
    patient_date_of_birth: string;
    patient_given_names: string;
    patient_name: string;
    record_created: string;
    record_origin: string;
  };
  dish_uuid: string;
  account_id: string;
  machine_id: string;
  created_at: string;
  updated_at: string;
}

interface SessionRecord {
  _id: string;
  dish_uuid: string;
  session_uuid: string;
  session_record: {
    available: boolean;
    dish_uuid: string;
    identifier_1: string;
    identifier_2: string;
    incubator: string;
    patient_date_of_birth: string;
    patient_given_names: string;
    patient_name: string;
    record_created: string;
    record_origin: string;
    session_end: string;
    session_start: string;
    session_uuid: string;
  };
  account_id: string;
  machine_id: string;
  created_at: string;
  updated_at: string;
}

interface Account {
  _id: string;
  name: string;
  account_name?: string;
}

interface Filter {
  patient_name: string;
  identifier_1: string;
  identifier_2: string;
  account_id: string;
  dish_uuid: string;
  _id: string;
}

const Geri: React.FC = () => {
  const navigate = useNavigate();
  const [dishRecords, setDishRecords] = useState<DishRecord[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filter, setFilter] = useState<Filter>({
    patient_name: "",
    identifier_1: "",
    identifier_2: "",
    account_id: "",
    dish_uuid: "",
    _id: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sessionsData, setSessionsData] = useState<{
    [key: string]: SessionRecord[];
  }>({});
  const [loadingSessions, setLoadingSessions] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    fetchDishRecords();
    fetchAccounts();
  }, [filter, page]);

  const fetchDishRecords = async () => {
    setIsLoading(true);
    try {
      const data = await ApiService.fetchGeriDishRecords({
        ...(filter?.patient_name && { patient_name: filter.patient_name }),
        ...(filter?.identifier_1 && { identifier_1: filter.identifier_1 }),
        ...(filter?.identifier_2 && { identifier_2: filter.identifier_2 }),
        ...(filter?.account_id && { account_id: filter.account_id }),
        ...(filter?.dish_uuid && { dish_uuid: filter.dish_uuid }),
        ...(filter?._id && { _id: filter._id }),
        page,
        pageSize,
      });
      setDishRecords(data?.data || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      console.error("Failed to fetch dish records:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await ApiService.fetchAccounts({});
      setAccounts(data?.data || []);
    } catch (err) {
      console.error("Failed to fetch accounts:", err);
    }
  };

  const fetchSessionsForDish = async (dishUuid: string) => {
    setLoadingSessions((prev) => new Set(prev).add(dishUuid));
    try {
      const data = await ApiService.fetchGeriSessionRecords({
        dish_uuid: dishUuid,
        page: 1,
        pageSize: 100, // Get all sessions for this dish
      });
      setSessionsData((prev) => ({
        ...prev,
        [dishUuid]: data?.data || [],
      }));
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setLoadingSessions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dishUuid);
        return newSet;
      });
    }
  };

  const toggleRow = async (dishUuid: string) => {
    const newExpandedRows = new Set(expandedRows);

    if (expandedRows.has(dishUuid)) {
      newExpandedRows.delete(dishUuid);
    } else {
      newExpandedRows.add(dishUuid);
      // Fetch sessions if not already loaded
      if (!sessionsData[dishUuid]) {
        await fetchSessionsForDish(dishUuid);
      }
    }

    setExpandedRows(newExpandedRows);
  };

  const handleFilterChange = (key: keyof Filter, value: string) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilter({
      patient_name: "",
      identifier_1: "",
      identifier_2: "",
      account_id: "",
      dish_uuid: "",
      _id: "",
    });
    setPage(1);
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    try {
      const blob = await ApiService.downloadGeriDishRecordsExcel();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "geri_dish_records.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download Excel:", error);
      alert("Failed to download Excel file. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          <button
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">
              {isDownloading ? "Downloading..." : "Download Excel"}
            </span>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Patient Name
            </label>
            <input
              type="text"
              value={filter.patient_name}
              onChange={(e) =>
                handleFilterChange("patient_name", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Search by name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Identifier 1
            </label>
            <input
              type="text"
              value={filter.identifier_1}
              onChange={(e) =>
                handleFilterChange("identifier_1", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Identifier 1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Identifier 2
            </label>
            <input
              type="text"
              value={filter.identifier_2}
              onChange={(e) =>
                handleFilterChange("identifier_2", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Identifier 2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dish UUID
            </label>
            <input
              type="text"
              value={filter.dish_uuid}
              onChange={(e) => handleFilterChange("dish_uuid", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Dish UUID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Record ID
            </label>
            <input
              type="text"
              value={filter._id}
              onChange={(e) => handleFilterChange("_id", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Record ID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account
            </label>
            <select
              value={filter.account_id}
              onChange={(e) => handleFilterChange("account_id", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">All Accounts</option>
              {accounts.map((account) => (
                <option key={account._id} value={account._id}>
                  {account.account_name || account.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Dish Records List */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Dish Records ({total})
          </h3>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading records...</p>
          </div>
        ) : dishRecords.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No Information Recorded.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Identifier 1
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Identifier 2
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dish UUID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Egg Age
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Record Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {dishRecords.map((record) => (
                    <React.Fragment key={record._id}>
                      {/* Main Dish Row */}
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleRow(record.dish_uuid)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {expandedRows.has(record.dish_uuid) ? (
                              <ChevronDown className="h-5 w-5" />
                            ) : (
                              <ChevronRight className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {record.dish_record.patient_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {record.dish_record.patient_given_names}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {record.dish_record.identifier_1 || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {record.dish_record.identifier_2 || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 font-mono text-xs">
                            {record.dish_uuid}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {record.dish_record.egg_age}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(
                              record.dish_record.record_created
                            ).toLocaleString()}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Sessions Section */}
                      {expandedRows.has(record.dish_uuid) && (
                        <tr>
                          <td colSpan={7} className="px-6 py-4 bg-gray-50">
                            {loadingSessions.has(record.dish_uuid) ? (
                              <div className="text-center py-8">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <p className="mt-2 text-sm text-gray-600">
                                  Loading sessions...
                                </p>
                              </div>
                            ) : sessionsData[record.dish_uuid]?.length === 0 ? (
                              <div className="text-center py-8 text-gray-500 text-sm">
                                No sessions found for this dish.
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                  Sessions (
                                  {sessionsData[record.dish_uuid]?.length || 0})
                                </h4>
                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                                          Session UUID
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                                          Incubator
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                                          Session Start
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                                          Session End
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {sessionsData[record.dish_uuid]?.map(
                                        (session) => (
                                          <tr
                                            key={session._id}
                                            className="hover:bg-gray-50"
                                          >
                                            <td className="px-4 py-3 text-xs font-mono text-gray-900">
                                              {session.session_uuid}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                              {session.session_record
                                                .incubator || "-"}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                              {session.session_record
                                                .session_start
                                                ? new Date(
                                                    session.session_record.session_start
                                                  ).toLocaleString()
                                                : "-"}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                              {session.session_record
                                                .session_end
                                                ? new Date(
                                                    session.session_record.session_end
                                                  ).toLocaleString()
                                                : "-"}
                                            </td>
                                            <td className="px-4 py-3">
                                              <button
                                                onClick={() =>
                                                  navigate(
                                                    `/geri/embryos/${session.session_uuid}`,
                                                    {
                                                      state: {
                                                        patientName:
                                                          record.dish_record
                                                            .patient_name,
                                                        identifier1:
                                                          record.dish_record
                                                            .identifier_1,
                                                        dishUuid:
                                                          record.dish_uuid,
                                                      },
                                                    }
                                                  )
                                                }
                                                className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-xs font-medium"
                                              >
                                                <Eye className="h-3 w-3 mr-1" />
                                                View Embryos
                                              </button>
                                            </td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {dishRecords.map((record) => (
                <div
                  key={record._id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Dish Card */}
                  <div className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Patient Name
                          </p>
                          <p className="text-sm font-semibold text-gray-900 mt-1">
                            {record.dish_record.patient_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {record.dish_record.patient_given_names}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleRow(record.dish_uuid)}
                          className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                          {expandedRows.has(record.dish_uuid) ? (
                            <ChevronDown className="h-5 w-5" />
                          ) : (
                            <ChevronRight className="h-5 w-5" />
                          )}
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Identifier 1
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.dish_record.identifier_1 || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Identifier 2
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.dish_record.identifier_2 || "-"}
                          </p>
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                          Dish UUID
                        </p>
                        <p className="text-xs text-gray-900 mt-1 font-mono break-all">
                          {record.dish_uuid}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Egg Age
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {record.dish_record.egg_age}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">
                            Created
                          </p>
                          <p className="text-sm text-gray-900 mt-1">
                            {new Date(
                              record.dish_record.record_created
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Sessions for Mobile */}
                  {expandedRows.has(record.dish_uuid) && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      {loadingSessions.has(record.dish_uuid) ? (
                        <div className="text-center py-6">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          <p className="mt-2 text-sm text-gray-600">
                            Loading sessions...
                          </p>
                        </div>
                      ) : sessionsData[record.dish_uuid]?.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 text-sm">
                          No sessions found for this dish.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold text-gray-700">
                            Sessions (
                            {sessionsData[record.dish_uuid]?.length || 0})
                          </h4>
                          {sessionsData[record.dish_uuid]?.map((session) => (
                            <div
                              key={session._id}
                              className="bg-white rounded-lg border border-gray-200 p-3 space-y-2"
                            >
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                  Session UUID
                                </p>
                                <p className="text-xs text-gray-900 mt-1 font-mono break-all">
                                  {session.session_uuid}
                                </p>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                                    Incubator
                                  </p>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {session.session_record.incubator || "-"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                                    Start
                                  </p>
                                  <p className="text-xs text-gray-900 mt-1">
                                    {session.session_record.session_start
                                      ? new Date(
                                          session.session_record.session_start
                                        ).toLocaleDateString()
                                      : "-"}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() =>
                                  navigate(
                                    `/geri/embryos/${session.session_uuid}`,
                                    {
                                      state: {
                                        patientName:
                                          record.dish_record.patient_name,
                                        identifier1:
                                          record.dish_record.identifier_1,
                                        dishUuid: record.dish_uuid,
                                      },
                                    }
                                  )
                                }
                                className="w-full mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors inline-flex items-center justify-center"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Embryos
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-700 font-medium">
                Page: <span className="font-bold">{page}</span> /{" "}
                <span className="font-bold">{totalPages}</span> | Total:{" "}
                <span className="font-bold">{total}</span> records
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ◀ Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm font-medium text-white bg-black rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next ▶
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Geri;
